import type { Exam, ExamType } from '@mathflat/domain/Exam'
import type { AllGradeSubject, SchoolType, Semester } from '@mathflat/domain/SchoolSystem'
import type { ExamWithStudentExams, StudentExam } from '@mathflat/domain/StudentExam'
import { addMonths, format, parse } from 'date-fns'
import { uniq } from 'lodash-es'
import { action, computed, makeObservable, observable } from 'mobx'

import type { WorksheetExportType } from '@/contentComponents/modal/Download/type'
import { examController } from '@/controller/exam.controller'
import type { Student, StudentWorksheet, Worksheet } from '@/dto'
import type { ExamSchedule } from '@/dto/Exam/ExamSchedule'
import {
  type AllTypeCurriculumCoreItem,
  makeAllTypeCurriculumCoreItem,
  type SubscribableCurriculumCoreItem,
  type UnionGradeCurriculumCoreItem,
} from '@/service/curriculumCore.model'
import { curriculumCoreService } from '@/service/curriculumCore.service'
import { worksheetPrintService } from '@/service/print'
import { downloadFileByUrl } from '@/utils/download'
import { commonErrorMessage } from '@/utils/toast'

export type ExamSchoolType = SchoolType
export type ExamGrade = AllGradeSubject
export type ExamSubject = '확률과 통계' | '미적분'
export type ExamSort = 'BY_GRADE' | 'BY_NAME' | 'BY_PROGRESS'

export type ExamStatusKR = '출제됨' | '미응시' | '응시중' | '제출완료' | '채점중' | '채점완료'
export type DashboardCounts = Record<ExamStatusKR, number>

type BaseCurriculum =
  | AllTypeCurriculumCoreItem
  | UnionGradeCurriculumCoreItem
  | SubscribableCurriculumCoreItem

export type CurriculumItem = Pick<
  BaseCurriculum,
  'trieKey' | 'labelShort' | 'subscribeStatus' | 'schoolType' | 'gradeSubject'
>

export type ExamTexts = {
  /** 시험 타이틀 (예: "KMM수학경시대회", "와이즈만 전국 수학학력평가") */
  title: string
  /** 프리오픈 메시지 (예: "KMM수학경시대회가 곧 시작됩니다!") */
  preOpenMessage: string
  /** 고등부 수상 안내 (예: "KMM고등모의고사는 평가기준이 다르므로, 수상내역은 별도 제공되지 않습니다.") */
  highSchoolAwardDescription: string
}

export abstract class BaseExamService {
  protected _examSchedule: ExamSchedule[] = []
  protected _selectedYearMonth: ExamSchedule['yearMonth'] | null = null
  protected _selectedExams: ExamWithStudentExams[] = []

  constructor() {
    makeObservable<
      this,
      '_examSchedule' | '_selectedYearMonth' | '_selectedExams' | '_classifyExamStatus'
    >(this, {
      _examSchedule: observable,
      _selectedYearMonth: observable,
      _selectedExams: observable,
      examSchedule: computed,
      selectedYearMonth: computed,
      selectedExams: computed,
      selectedExamSchedule: computed,
      nextExamSchedule: computed,
      isSelectedExamOpened: computed,
      isCurrentExamOpened: computed,
      currentExam: computed,
      examYears: computed,
      selectYearMonth: action.bound,
      _classifyExamStatus: action,
    })
  }

  // Getters
  get examSchedule() {
    return [...this._examSchedule]
  }

  get selectedYearMonth() {
    return this._selectedYearMonth
  }

  get selectedExams() {
    return [...this._selectedExams]
  }

  get selectedExamSchedule() {
    return this._examSchedule.find((schedule) => schedule.yearMonth === this.selectedYearMonth)
  }

  get nextExamSchedule() {
    if (!this.selectedYearMonth) return undefined
    const nextYearMonth = addMonths(parse(this.selectedYearMonth, 'yyyy-MM', new Date()), 1)
    const nextYearMonthStr = format(nextYearMonth, 'yyyy-MM')
    return this._examSchedule.find((schedule) => schedule.yearMonth === nextYearMonthStr)
  }

  get isSelectedExamOpened() {
    return this.selectedExamSchedule?.isOpened ?? false
  }

  /** 선택한 회차와 무관하게, 현재 회차가 실제 응시 가능 기간인지 */
  get isCurrentExamOpened() {
    return this.currentExam?.isOpened ?? false
  }

  get currentExam() {
    return this._examSchedule.find((examSchedule) => examSchedule.isCurrentExam)
  }

  readonly currentDate = new Date()

  get examYears() {
    if (this.examSchedule.length === 0) return []

    const yearOfSchedules = this.examSchedule.map((schedule) => schedule.year)

    if (yearOfSchedules.length === 0) {
      return [this.currentDate.getFullYear()]
    }

    return uniq(yearOfSchedules).sort((a, b) => b - a)
  }

  // 공통 메서드: Curriculum
  getCurriculums = (
    params: { isForceExpanded?: boolean; includeHighSchool?: boolean } = {
      isForceExpanded: false,
      includeHighSchool: true,
    },
  ) => {
    const rawCurriculum = params.includeHighSchool
      ? curriculumCoreService.getKmmCurriculum()
      : curriculumCoreService.getKmmCurriculum().filter((item) => item.schoolType !== 'HIGH')

    const seen = new Set<string>()
    const filteredCurriculum = rawCurriculum.filter((item) => {
      const key = `${item.schoolType ?? ''}-${String(item.gradeSubject)}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    const result = params.isForceExpanded
      ? [
          makeAllTypeCurriculumCoreItem({
            schoolType: null,
            label: '전체',
          }),
          ...filteredCurriculum,
        ]
      : filteredCurriculum

    return result.map((item) => {
      const labelShort =
        item.gradeSubject === '확률과 통계' || item.gradeSubject === '미적분'
          ? `고3(${item.gradeSubject})`
          : item.labelShort
      return { ...item, labelShort, subscribeStatus: item.subscribeStatus }
    })
  }

  getCurriculumsBySchedule = (
    params: { isForceExpanded?: boolean } = { isForceExpanded: false },
  ) => {
    const includeHighSchool = this.selectedExamSchedule?.hasHighExam ?? false
    return this.getCurriculums({ ...params, includeHighSchool })
  }

  // 공통 메서드: 시험 조회
  async selectYearMonth(value: ExamSchedule['yearMonth']) {
    this._selectedYearMonth = value
    const exam = await this.fetchExamByYearMonth(value)
    return { hasHighExam: exam.some((exam) => exam.schoolType === 'HIGH') }
  }

  getExamBySchoolGrade(
    schoolType?: ExamSchoolType,
    grade?: ExamGrade,
    semester?: NonNullable<Semester>,
  ) {
    if (!schoolType || !grade) return

    return this.selectedExams.find((exam) => {
      const matchSchoolGrade =
        exam.schoolType === schoolType &&
        (exam.grade.toString() === grade || exam.subject === grade)

      // semester가 제공된 경우 semester도 비교 (exam에 semester가 있는 경우만)
      if (semester && 'semester' in exam) {
        return (
          matchSchoolGrade && (exam as { semester?: NonNullable<Semester> }).semester === semester
        )
      }
      return matchSchoolGrade
    })
  }

  getAssignedExam(id: Exam['id']) {
    return this.selectedExams.find((exam) =>
      exam.studentExams.some((studentExam) => studentExam.id === id),
    )
  }

  getAssignedExamsByStudentId(studentId: Student['id'], schoolType?: ExamSchoolType) {
    const assignedExams = this.selectedExams.filter((exam) =>
      exam.studentExams.some((studentExam) => studentExam.studentId === studentId),
    )
    if (schoolType) return assignedExams.filter((exam) => exam.schoolType === schoolType)
    return assignedExams
  }

  // 공통 메서드: 대시보드
  protected _classifyExamStatus(
    studentExam: ExamWithStudentExams['studentExams'][number],
  ): ExamStatusKR {
    if (studentExam.status === 'INCOMPLETE') return '미응시'
    if (studentExam.scoringStatus === 'IN_PROGRESS') return '채점중'
    if (studentExam.scoringStatus === 'COMPLETED') return '채점완료'
    if (studentExam.status === 'COMPLETE' || studentExam.status === 'EXPIRED') return '제출완료'
    return '응시중'
  }

  protected _getCountExamsByStatus(
    studentExams: ExamWithStudentExams['studentExams'],
  ): DashboardCounts {
    const statusCounts = studentExams.reduce(
      (acc, student) => {
        const status = this._classifyExamStatus(student)
        acc[status] = (acc[status] || 0) + 1
        return acc
      },
      {} as Record<ExamStatusKR, number>,
    )

    return {
      출제됨: studentExams.length,
      응시중: statusCounts['응시중'] || 0,
      채점중: statusCounts['채점중'] || 0,
      제출완료: statusCounts['제출완료'] || 0,
      채점완료: statusCounts['채점완료'] || 0,
      미응시: statusCounts['미응시'] || 0,
    }
  }

  getDashboardCounts(
    schoolType?: ExamSchoolType,
    gradeSubject?: AllGradeSubject,
    semester?: NonNullable<Semester>,
  ): DashboardCounts {
    const isAll = !schoolType && !gradeSubject
    const studentExams = isAll
      ? this.selectedExams.flatMap((exam) => exam.studentExams)
      : this.getExamBySchoolGrade(schoolType, gradeSubject, semester)?.studentExams ?? []
    return this._getCountExamsByStatus(studentExams)
  }

  // 공통 메서드: 인쇄
  async printExam(targetExam: {
    worksheetId: Worksheet['id']
    title: Worksheet['title']
    assignList: {
      studentId: Student['id']
      studentName: Student['name']
      studentWorksheetId: StudentWorksheet['id']
    }[]
    isPracticeTest?: boolean
    exportType?: WorksheetExportType
  }) {
    const type = targetExam.exportType ?? (targetExam.isPracticeTest ? 'KMM_PRACTICE_TEST' : 'KMM')
    worksheetPrintService.openWorksheetPrintModal({
      worksheets: [
        {
          id: targetExam.worksheetId,
          title: targetExam.title,
          assignedStudentWorksheetList: targetExam.assignList.map((assignedStudent) => ({
            studentId: assignedStudent.studentId,
            studentName: assignedStudent.studentName,
            studentWorksheetId: assignedStudent.studentWorksheetId,
          })),
        },
      ],
      afterPrintFn: () => {},
      ignoredForDeleted: true,
      type,
      description: '시험지(상장, 결과지)는 PC에서 인쇄해주세요',
    })
  }

  // 추상 메서드 (각 서비스에서 구현)
  abstract initialize(): Promise<void>
  abstract fetchExamByYearMonth(
    yearMonth: ExamSchedule['yearMonth'],
  ): Promise<ExamWithStudentExams[]>
  abstract downloadReports(examId: Exam['id']): Promise<void>

  async downloadAllReports(examType: ExamType) {
    if (!this.selectedYearMonth) return
    try {
      const { presignedUrl, fileName } = await examController.getReport({
        type: 'EXAM_SCHEDULE',
        yearMonth: this.selectedYearMonth,
        examType,
      })
      await downloadFileByUrl(presignedUrl, { fullFileName: fileName })
    } catch (error) {
      commonErrorMessage(error)
    }
  }
  abstract downloadStudentReport(
    examId: Exam['id'],
    studentId: StudentExam['studentId'],
  ): Promise<void>
  abstract assignExamToStudent(params: {
    schoolType: ExamSchoolType
    grade: ExamGrade
    semester?: NonNullable<Semester>
    studentIdList: StudentExam['studentId'][]
  }): Promise<void>
  abstract unassignExamFromStudent(studentDataRelationIds: StudentExam['id'][]): Promise<void>
}
